/**
 * Connect > Partners directory at /connect.
 *
 * Table of every wholesale / broker partner org with type, status, Sage
 * account count, user count, and created date. Filters: q, org_type, status.
 * "New partner" opens the onboarding wizard. A row navigates to the detail page.
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { formatInstantDate } from '@/lib/dateUtils';
import {
  hasConnectPartners,
  isNotAvailable,
  orgStatusTone,
  orgTypeLabel,
  useConnectOrgs,
  ORG_TYPE_TONES,
  type ConnectOrg,
  type OrgType,
} from '@/lib/useConnect';
import { AccessDeniedCard, ConnectHeader, NotAvailableCard, secondaryBtnCls } from './connectShared';
import { OnboardingWizard } from './OnboardingWizard';

export function ConnectPartners() {
  const { user } = useSession();
  // No more read/write levels: holding connect.partners grants both.
  const canView = hasConnectPartners(user?.permissions, user?.superAdmin);
  const canWrite = canView;

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const orgType = (searchParams.get('org_type') ?? '') as OrgType | '';
  const status = searchParams.get('status') ?? '';

  const setStr = (k: string, v: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v) next.set(k, v);
        else next.delete(k);
        return next;
      },
      { replace: true },
    );

  const [wizardOpen, setWizardOpen] = useState(false);

  const orgsQ = useConnectOrgs({ q: q || undefined, org_type: orgType || undefined, status: status || undefined });

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const o of orgsQ.orgs) if (o.status) set.add(o.status);
    return Array.from(set).sort();
  }, [orgsQ.orgs]);

  if (user && !canView) return <AccessDeniedCard />;

  const backendMissing = isNotAvailable(orgsQ.error);

  const filterBar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="search" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Search</label>
        <ClearableSearchInput
          id="search"
          value={q}
          onChange={(v) => setStr('q', v)}
          placeholder="Partner name..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <div>
        <label htmlFor="org_type" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Type</label>
        <select
          id="org_type"
          value={orgType}
          onChange={(e) => setStr('org_type', e.target.value)}
          className="min-w-[140px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          <option value="wholesaler">Wholesaler</option>
          <option value="broker">Broker</option>
        </select>
      </div>
      <div>
        <label htmlFor="status" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Status</label>
        <select
          id="status"
          value={status}
          onChange={(e) => setStr('status', e.target.value)}
          className="min-w-[140px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>
      {(q || orgType || status) && (
        <button type="button" onClick={() => setSearchParams({}, { replace: true })} className={secondaryBtnCls}>
          Reset filters
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ConnectHeader
        title="Partners"
        subtitle="Wholesale and broker organizations in the partner portal. Click a partner to manage its Sage mappings, users, and agreements."
        actions={
          <>
            <ToolPinToggle toolSlug="connect-partners" toolName="Partners" />
            {canWrite && (
              <button type="button" onClick={() => setWizardOpen(true)} className={secondaryBtnCls}>
                <Plus className="h-3 w-3" /> New partner
              </button>
            )}
          </>
        }
      />

      {backendMissing ? (
        <NotAvailableCard what="The partners directory" />
      ) : (
        <DataGridShell
          total={orgsQ.orgs.length}
          loaded={orgsQ.orgs.length}
          countNoun="partners"
          toolbar={filterBar}
        >
          <table className="min-w-full text-sm">
            <thead className={gridTheadClassName}>
              <tr>
                <th className="px-3 py-2 text-left font-medium">Partner</th>
                <th className="px-3 py-2 text-left font-medium w-[130px]">Type</th>
                <th className="px-3 py-2 text-left font-medium w-[120px]">Status</th>
                <th className="px-3 py-2 text-right font-medium w-[120px]">Sage accounts</th>
                <th className="px-3 py-2 text-right font-medium w-[90px]">Users</th>
                <th className="px-3 py-2 text-left font-medium w-[120px]">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {orgsQ.isLoading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading partners...</td></tr>
              ) : orgsQ.error ? (
                <tr><td colSpan={6} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {orgsQ.error.message}</td></tr>
              ) : orgsQ.orgs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No partners match this filter.</td></tr>
              ) : (
                orgsQ.orgs.map((org) => <PartnerRow key={org.id} org={org} />)
              )}
            </tbody>
          </table>
        </DataGridShell>
      )}

      {wizardOpen && <OnboardingWizard onClose={() => setWizardOpen(false)} />}
    </div>
  );
}

function PartnerRow({ org }: { org: ConnectOrg }) {
  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        <Link to={`/connect/partners/${org.id}`} className="hover:underline font-medium">{org.name}</Link>
      </td>
      <td className="px-3 py-2">
        <Pill tone={ORG_TYPE_TONES[org.org_type]}>{orgTypeLabel(org.org_type)}</Pill>
      </td>
      <td className="px-3 py-2">
        <Pill tone={orgStatusTone(org.status)}>{org.status || '--'}</Pill>
      </td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{org.sage_customers?.length ?? 0}</td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{org.user_count}</td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatInstantDate(org.created_at)}</td>
    </tr>
  );
}
